import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import { httpOk, GET, POST, PUT, FILE, resolveUploadUrl } from 'helpers/api'
import MobileAppPreview from './MobileAppPreview'
import ThemePicker, { defaultTheme } from 'pages/parts/ThemePicker'
import ChannelPosesMenu, { saveChannelPosesMenu } from 'pages/parts/ChannelPosesMenu'
import HintIcon from 'pages/parts/HintIcon'
import PageTabs from 'pages/parts/PageTabs'
import { checkImage } from 'helpers/imageRules'
import { toast } from 'react-toastify'
import { Link } from 'react-router'

const UPLOAD_PATH = 'mobile-apps'

const RELEASE_STATUSES = {
	0: { key: 'mobile_app_release_draft', className: 'bg-secondary' },
	1: { key: 'mobile_app_release_requested', className: 'bg-info' },
	2: { key: 'mobile_app_release_building', className: 'bg-warning' },
	3: { key: 'mobile_app_release_published', className: 'bg-success' },
	4: { key: 'mobile_app_release_removed', className: 'bg-danger' },
}

const defaultData = {
	name: '',
	brand_name: '',
	slogan: '',
	pos_id: null,
	description: '',
	logo: '',
	header_logo: '',
	theme_id: null,
	colors: null,
	themes: [],
	min_order_amount: 0,
	is_active: true,
	languages: [],
	poses: [],
	external_menu_id: null,
	order_types: [],
	payment_types: [],
	payment_type_id: null,
	release_status: 0,
	settings: {
		external_menu_id: null,
	},
}

function mergeOrderTypes(savedOrderTypes = [], allOrderTypes = []) {
	return allOrderTypes.map((type) => {
		const orderTypeId = Number(type.id)
		const saved = (savedOrderTypes ?? []).find((item) => (
			Number(item.order_type_id ?? item.id) === orderTypeId
		))

		return {
			...type,
			order_type_id: orderTypeId,
			order_type_sub_id: saved?.order_type_sub_id ?? null,
		}
	})
}

function MobileAppBuilder() {
	const { t } = useTranslation()

	const [data, setData] = useState(defaultData)
	const [activeTab, setActiveTab] = useState('general')
	const [copied, setCopied] = useState(false)
	const [poses, setPoses] = useState([])
	const [languages, setLanguages] = useState([])
	const [deliveryRules, setDeliveryRules] = useState([])
	const [orderTypeSubs, setOrderTypeSubs] = useState([])
	const [paymentTypes, setPaymentTypes] = useState([])
	const [externalMenus, setExternalMenus] = useState([])
	const [priceCategories, setPriceCategories] = useState([])
	const [posMenus, setPosMenus] = useState({})

	async function load() {
		const response = await GET('/chain/mobile-app', {}, { loader: true })
		if (httpOk(response)) {
			const payload = {
				...defaultData,
				...response.data,
				settings: {
					...defaultData.settings,
					...(response.data.settings ?? {}),
				},
			}

			if (!payload.theme_id && payload.themes?.length) {
				const theme = defaultTheme(payload.themes)
				payload.theme_id = theme?.id ?? null
				payload.colors = theme?.colors ?? null
			}
			const responseOrderTypes = await GET('/helper/order-types')
			if (httpOk(responseOrderTypes)) {
				payload.order_types = mergeOrderTypes(payload.order_types, responseOrderTypes.data)
			}
			setData(payload)
		}
	}

	async function loadHelpers() {
		const [posesResponse, languagesResponse, deliveryRulesResponse, orderTypesResponse, paymentTypesResponse, externalMenusResponse, priceCategoriesResponse] = await Promise.all([
			GET('/helper/poses'),
			GET('/helper/languages'),
			GET('/helper/delivery-rules'),
			GET('/helper/order-type-subs'),
			GET('/helper/payment-types'),
			GET('/helper/external-menu'),
			GET('/price-categories'),
		])

		if (httpOk(posesResponse)) setPoses(posesResponse.data)
		if (httpOk(languagesResponse)) setLanguages(languagesResponse.data)
		if (httpOk(deliveryRulesResponse)) setDeliveryRules(deliveryRulesResponse.data)
		if (httpOk(orderTypesResponse)) setOrderTypeSubs(orderTypesResponse.data)
		if (httpOk(paymentTypesResponse)) setPaymentTypes(paymentTypesResponse.data)
		if (httpOk(externalMenusResponse)) setExternalMenus(externalMenusResponse.data)
		if (httpOk(priceCategoriesResponse)) setPriceCategories(priceCategoriesResponse.data?.data ?? [])
	}

	async function save(e) {
		e.preventDefault()
		const payload = {
			...data,
			order_types: (data.order_types ?? []).map((item) => ({
				order_type_id: item.order_type_id ?? item.id,
				order_type_sub_id: item.order_type_sub_id,
			})),
		}
		const response = data.id
			? await PUT('/chain/mobile-app', payload, { loader: true, notification: true })
			: await POST('/chain/mobile-app', payload, { loader: true, notification: true })

		if (httpOk(response)) {
			await saveChannelPosesMenu(posMenus)
			load()
		}
	}

	function change(key, value) {
		setData(prev => ({ ...prev, [key]: value }))
	}

	function changeTheme(option) {
		setData(prev => ({
			...prev,
			theme_id: option?.id ?? null,
			colors: option?.colors ?? null,
		}))
	}

	function changeDefaultPos(option) {
		const posId = option?.id ?? null
		const currentPoses = data.poses?.length ? data.poses : poses
		const appPoses = currentPoses.map(item => (
			Number(item.id) === Number(posId) ? { ...item, status: true } : item
		))

		setData(prev => ({
			...prev,
			pos_id: posId,
			poses: appPoses,
		}))
	}

	function changePosStatus(posId, checked) {
		const currentPoses = data.poses?.length ? data.poses : poses
		const appPoses = currentPoses.map(item => (
			item.id === posId ? { ...item, status: checked } : item
		))
		setData({ ...data, poses: appPoses })
	}

	function handleSelectOrderTypeSub(option, index) {
		const orderTypes = [...data.order_types]
		orderTypes[index] = { ...orderTypes[index], order_type_sub_id: option.id }
		setData({ ...data, order_types: orderTypes })
	}

	// Идентификатор разработчик вставляет в сборку — читать его с экрана глазами
	// значит рано или поздно ошибиться в одном символе из тридцати шести.
	async function copyUuid() {
		if (!data.uuid) return

		try {
			await navigator.clipboard.writeText(data.uuid)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch (error) {
			// Буфер недоступен (http без localhost, старый браузер) — выделяем,
			// чтобы скопировать руками, а не оставляем кнопку молча мёртвой.
			window.prompt(t('mobile_app_identifier'), data.uuid)
		}
	}

	async function uploadImageFile(file, target, index = null) {
		if (!file) return
		const formData = new FormData()
		formData.append('image', file)
		formData.append('path', UPLOAD_PATH)
		// Иконка уходит в стор как есть: без флага сервер уменьшает картинку до 1200
		// и пересохраняет JPEG с качеством 70 — на краях логотипа лезут артефакты
		if (target === 'logo') formData.append('original', '1')

		const response = await FILE('/upload-image-only', formData, { loader: true })
		if (!httpOk(response)) return

		change(target, response.data.url)
	}

	// Иконка, сплеш и логотип шапки — через проверку формы и размера (helpers/imageRules):
	// неподходящий файл не уходит вовсе.
	async function pickImage(file, target, index) {
		if (!file) return

		const checked = await checkImage(file, target)
		if (checked.error) {
			toast.error(t(checked.error, checked.params))
			return
		}
		uploadImageFile(checked.file, target, index)
	}

	async function uploadImage(e, target, index = null) {
		pickImage(e.target.files?.[0], target, index)
		e.target.value = ''
	}

	async function dropImage(e, target, index = null) {
		e.preventDefault()
		pickImage(e.dataTransfer.files?.[0], target, index)
	}

	useEffect(() => {
		load()
		loadHelpers()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const activePoses = data.poses?.length ? data.poses : poses
	const menuOptions = [
		{ id: null, name: t('main_menu') },
		...externalMenus,
	]
	const selectedMenuOption = data.external_menu_id
		? menuOptions.find(option => Number(option.id) === Number(data.external_menu_id))
		: menuOptions[0]
	const selectedPosOption = data.pos_id
		? activePoses.find(option => Number(option.id) === Number(data.pos_id))
		: null
	const releaseStatus = RELEASE_STATUSES[data.release_status ?? 0] ?? RELEASE_STATUSES[0]
	const uploadUrl = path => resolveUploadUrl(path, UPLOAD_PATH)

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">{t('mobile_app_constructor')}</h4>
				<button className="btn btn-primary" onClick={save}>{t('save')}</button>
			</div>

			<PageTabs
				active={activeTab}
				onChange={setActiveTab}
				tabs={[
					{ key: 'general', label: t('general') },
					{ key: 'theme', label: t('theme') },
					{ key: 'orders', label: t('orders') },
					{ key: 'banners', label: t('banners') },
					{ key: 'poses', label: t('poses') },
					{ key: 'publication', label: t('mobile_app_publication') },
					{ key: 'preview', label: t('preview') },
				]} />

			{activeTab === 'preview'
				? <MobileAppPreview uuid={data.uuid} />
				: <form className="page-compact" onSubmit={save} autoComplete="off">
					<div className={activeTab !== 'general' ? 'd-none' : 'card'}>
						<div className="card-body">
							<div className="row">
								<div className="col-lg-6">
									<div className="row">
										<div className="col-sm-6 form-group">
											<label>{t('name2')}<span className="required-mark">*</span></label>
											<input className="form-control" value={data.name ?? ''} onChange={e => change('name', e.target.value)} />
										</div>
										<div className="col-sm-6 form-group">
											<label>{t('brand_name')}</label>
											<input className="form-control" value={data.brand_name ?? ''} onChange={e => change('brand_name', e.target.value)} />
										</div>
									</div>
									{/* Слоган приложение пишет текстом под логотипом на сплеше — картинку рисовать не нужно */}
									<div className="form-group">
										<label>{t('mobile_app_slogan')}</label>
										<input className="form-control" maxLength={80}
											placeholder={t('mobile_app_slogan_placeholder')}
											value={data.slogan ?? ''}
											onChange={e => change('slogan', e.target.value)} />
										<small className="text-muted">{t('mobile_app_slogan_hint')}</small>
									</div>
									<div className="form-group">
										<label>{t('description')}</label>
										<textarea className="form-control" rows="3" value={data.description ?? ''} onChange={e => change('description', e.target.value)} />
									</div>
									<div className="form-group mb-0">
										<label className="d-block">{t('mobile_app_orders')}</label>
										<div className="form-check form-switch">
											<input
												type="checkbox"
												className="form-check-input"
												checked={!!data.is_active}
												onChange={e => change('is_active', e.target.checked)}
											/>
											<span className="ms-2">{data.is_active ? t('mobile_app_orders_on') : t('mobile_app_orders_off')}</span>
										</div>
									</div>
								</div>
								<div className="col-lg-6">
									<div className="form-group">
										<label>{t('default_pos')}<span className="required-mark">*</span></label>
										<Select
											options={activePoses}
											value={selectedPosOption || null}
											onChange={changeDefaultPos}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.id}
											placeholder=""
										/>
									</div>
									<div className="form-group">
										<label>{t('mobile_app_menu')}<span className="required-mark">*</span></label>
										<Select
											options={menuOptions}
											value={selectedMenuOption || null}
											onChange={option => {
												change('external_menu_id', option?.id ?? null)
											}}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.id ?? 'main'}
											placeholder=""
										/>
										<Select
											isClearable
											className="mt-2"
											options={priceCategories}
											value={priceCategories.find(option => option.id === data.price_category_id) || ''}
											onChange={option => change('price_category_id', option ? option.id : null)}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.id}
											placeholder={t('base_price')}
										/>
									</div>
									<div className="form-group">
										<label>{t('default_language')}<span className="required-mark">*</span></label>
										<Select
											options={languages}
											value={languages.find(option => option.id === data.default_language_id) || null}
											onChange={option => change('default_language_id', option ? option.id : null)}
											getOptionLabel={option => `${option.native_name ?? option.name} (${option.code})`}
											getOptionValue={option => option.id}
											placeholder=""
										/>
									</div>
									<div className="form-group mb-0">
										<label>{t('mobile_app_languages')}<span className="required-mark">*</span></label>
										<Select
											isMulti
											options={languages}
											value={data.languages ?? []}
											onChange={options => change('languages', options ?? [])}
											getOptionLabel={option => `${option.native_name ?? option.name} (${option.code})`}
											getOptionValue={option => option.id}
											placeholder=""
										/>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className={activeTab !== 'theme' ? 'd-none' : 'card'}>
						<div className="card-body">
							<ThemePicker
								title={t('theme')}
								required
								themes={data.themes ?? []}
								value={data.theme_id}
								onChange={changeTheme}
							/>
							<div className="small text-muted mt-3">{t('mobile_app_theme_hint')}</div>
						</div>
					</div>

					<div className={activeTab !== 'orders' ? 'd-none' : 'card'}>
						<div className="card-body">
							<div className="row">
								<div className="col-md-6 col-xl-4">
									<small className="text-muted d-block mb-2">{t('delivery')}</small>
									<div className="form-group">
										<label>{t('delivery_rules')}<span className="required-mark">*</span></label>
										<Select
											options={deliveryRules}
											value={deliveryRules.find(option => option.id === data.delivery_rule_id) || null}
											onChange={option => change('delivery_rule_id', option ? option.id : null)}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.id}
											placeholder=""
										/>
									</div>
									<div className="form-group mb-0">
										<label>{t('delivery_type')}<span className="required-mark">*</span></label>
										<div className="d-flex flex-column gap-2">
											{data?.order_types?.map((item, index) => (
												<div key={item.order_type_id ?? item.id ?? index} className="d-flex align-items-center gap-2">
													<span className="fw-600 flex-shrink-0" style={{ minWidth: 90 }}>{item.name}</span>
													<Select
														className="flex-grow-1"
														options={orderTypeSubs}
														value={orderTypeSubs.find(option => option.id === item.order_type_sub_id) || null}
														onChange={option => handleSelectOrderTypeSub(option, index)}
														getOptionLabel={option => option.name}
														getOptionValue={option => option.id}
														placeholder=""
													/>
												</div>
											))}
										</div>
									</div>
								</div>
								<div className="col-md-6 col-xl-4">
									<small className="text-muted d-block mb-2">{t('payment_types')}</small>
									<div className="form-group">
										<label>{t('payment_types')}<span className="required-mark">*</span></label>
										<Select
											isMulti
											options={paymentTypes}
											value={data.payment_types ?? []}
											onChange={options => change('payment_types', options ?? [])}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.id}
											placeholder=""
										/>
									</div>
									<div className="form-group">
										<label>{t('by_default')}<span className="required-mark">*</span></label>
										<Select
											options={paymentTypes}
											value={paymentTypes.find(option => option.payment_type_id === data.payment_type_id) || null}
											onChange={option => change('payment_type_id', option ? option.payment_type_id : null)}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.payment_type_id}
											placeholder=""
										/>
									</div>
									<div className="form-group mb-0">
										<label>{t('min_order_amount')}</label>
										<input
											type="number"
											min="0"
											className="form-control"
											value={data.min_order_amount ?? 0}
											onChange={e => change('min_order_amount', e.target.value)}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Баннеры общие на бренд: грузятся один раз в «Бренд → Баннеры», канал отмечается галочкой */}
					<div className={activeTab !== 'banners' ? 'd-none' : 'card'}>
						<div className="card-body">
							<p className="mb-3">{t('brand_banners_moved')}</p>
							<Link to="/brand" className="btn btn-primary">{t('brand_banners_open')}</Link>
						</div>
					</div>

					<div className={activeTab !== 'poses' ? 'd-none' : 'card'}>
						<div className="card-body">
							<ChannelPosesMenu
								poses={activePoses}
								overrides={posMenus}
								setOverrides={setPosMenus}
								onToggle={(e, index) => changePosStatus(activePoses[index].id, e.target.checked)} />
						</div>
					</div>

					{/* Публикация — только на чтение. Package и версии ведёт админ: смена
						package у выпущенного приложения оторвала бы установленные сборки. */}
					<div className={activeTab !== 'publication' ? 'd-none' : 'card'}>
						<div className="card-body">
							<div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
								<h5 className="mb-0">{t('mobile_app_publication')}</h5>
								<span className={`badge ${releaseStatus.className}`}>{t(releaseStatus.key)}</span>
							</div>

							{data.uuid
								? <div className="row">
									<div className="col-md-6 col-xl-3 form-group">
										<label className="text-muted">{t('mobile_app_identifier')}<HintIcon text={t('mobile_app_identifier_hint')} /></label>
										<div className="d-flex align-items-start gap-2">
											<code className="text-break flex-grow-1">{data.uuid}</code>
											<button
												type="button"
												className="btn btn-sm btn-outline-primary flex-shrink-0"
												title={t('copy')}
												onClick={copyUuid}>
												<i className={copied ? 'uil-check' : 'uil-copy'} />
											</button>
										</div>
									</div>
									<div className="col-md-6 col-xl-3 form-group">
										<label className="text-muted">Android (package)</label>
										<div className="text-break">{data.android_package || '—'}</div>
										{!!data.latest_build_android &&
											<small className="text-muted">{t('mobile_app_build')} {data.latest_build_android}</small>
										}
									</div>
									<div className="col-md-6 col-xl-3 form-group">
										<label className="text-muted">iOS (bundle id)</label>
										<div className="text-break">{data.ios_bundle_id || '—'}</div>
										{!!data.latest_build_ios &&
											<small className="text-muted">{t('mobile_app_build')} {data.latest_build_ios}</small>
										}
									</div>
									<div className="col-md-6 col-xl-3 form-group">
										<label className="text-muted">{t('mobile_app_store_links')}</label>
										<div className="d-flex flex-column">
											{data.android_store_url
												? <a href={data.android_store_url} target="_blank" rel="noreferrer">Google Play</a>
												: <span className="text-muted">Google Play — —</span>
											}
											{data.ios_store_url
												? <a href={data.ios_store_url} target="_blank" rel="noreferrer">App Store</a>
												: <span className="text-muted">App Store — —</span>
											}
										</div>
									</div>
								</div>
								: <div className="text-muted">{t('mobile_app_publication_empty')}</div>
							}
							<div className="border-top pt-3 mt-3">
								<h6 className="mb-1">{t('mobile_app_store_assets')}</h6>
								<div className="small text-muted mb-3">{t('mobile_app_assets_hint')}</div>
								<div className="row">
									<div className="col-lg-6 col-xl-4">
										<div className="row">
											<div className="col-6 form-group mb-0">
												<label>{t('mobile_app_icon')}</label>
												<div className="border rounded overflow-hidden bg-light">
													<label
														className="d-flex align-items-center justify-content-center mb-0 cursor"
														onDragOver={e => e.preventDefault()}
														onDrop={e => dropImage(e, 'logo')}
														style={{
															minHeight: 150,
															background: data.logo ? `url(${uploadUrl(data.logo)}) center / contain no-repeat` : '#f8f9fa',
														}}>
														{!data.logo &&
															<div className="text-center text-muted px-2">
																<i className="fa fa-image d-block mb-2" style={{ fontSize: 24 }} />
																<span className="small">{t('mobile_app_icon')}</span>
																<div className="small mt-1">1024×1024 PNG</div>
															</div>
														}
														<input type="file" className="d-none" accept="image/png,image/jpeg" onChange={e => uploadImage(e, 'logo')} />
													</label>
													<div className="d-flex justify-content-end p-2 bg-white">
														<button type="button" className="btn btn-sm btn-outline-danger" disabled={!data.logo} onClick={() => change('logo', '')}>
															{t('delete')}
														</button>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>

							{/* Рядом с иконкой, но не в сборке: логотип шапки приходит в config
								и меняется сразу, без выпуска новой версии */}
							<div className="border-top pt-3 mt-3">
								<h6 className="mb-1">{t('mobile_app_header_logo')}</h6>
								<div className="small text-muted mb-3">{t('mobile_app_header_logo_hint')}</div>
								<div className="row">
									<div className="col-md-8 col-xl-5 form-group mb-0">
										<div className="border rounded overflow-hidden">
											<label
												className="d-flex align-items-center justify-content-center mb-0 cursor"
												onDragOver={e => e.preventDefault()}
												onDrop={e => dropImage(e, 'header_logo')}
												style={{
													minHeight: 110,
													background: data.header_logo ? `#f7f8fa url(${uploadUrl(data.header_logo)}) center / auto 56px no-repeat` : '#f8f9fa',
												}}>
												{!data.header_logo &&
													<div className="text-center text-muted px-2">
														<i className="fa fa-image d-block mb-2" style={{ fontSize: 24 }} />
														<span className="small">{t('mobile_app_header_logo')}</span>
														<div className="small mt-1">{t('mobile_app_header_logo_format')}</div>
													</div>
												}
												<input type="file" className="d-none" accept="image/png,image/webp" onChange={e => uploadImage(e, 'header_logo')} />
											</label>
											{/* Тот же логотип на тёмной теме приложения: цветной знак там не перекрашивается */}
											{!!data.header_logo &&
												<div style={{ height: 70, background: `#1a1a1a url(${uploadUrl(data.header_logo)}) center / auto 40px no-repeat` }} />
											}
											<div className="d-flex justify-content-end p-2 bg-white">
												<button type="button" className="btn btn-sm btn-outline-danger" disabled={!data.header_logo} onClick={() => change('header_logo', '')}>
													{t('delete')}
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</form>
			}
		</>
	)
}

export default MobileAppBuilder
